import {useState, useMemo} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import Swal from 'sweetalert2'
import '../AuthPageStyle.scss'

const initialValues = {
  phone: '',
  password: '',
}

export function Login() {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const {saveAuth, setCurrentUser} = useAuth()

  const loginSchema = useMemo(
    () =>
      Yup.object().shape({
        phone: Yup.string()
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.PHONE_REQUIRED'}))
          .test(
            'is-valid-identifier',
            intl.formatMessage({id: 'AUTH.VALIDATION.PHONE_INVALID'}),
            (value) => {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
              const phoneRegex = /^09\d{9}$/
              return emailRegex.test(value || '') || phoneRegex.test(value || '')
            }
          ),
        password: Yup.string()
          .min(8, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MIN'}))
          .max(50, intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_MAX'}))
          .matches(
            /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/,
            intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_PATTERN'})
          )
          .required(intl.formatMessage({id: 'AUTH.VALIDATION.PASSWORD_REQUIRED'})),
      }),
    [intl]
  )

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.phone, values.password)
        if (auth.status) {
          saveAuth(auth)
          const {data: user} = await getUserByToken()
          if (user) {
            setCurrentUser(user)
            Swal.fire({
              title: intl.formatMessage({id: 'AUTH.LOGIN.SUCCESS_TITLE'}),
              text: intl.formatMessage({id: 'AUTH.LOGIN.SUCCESS_TEXT'}),
              icon: 'success',
              timer: 2000,
              confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.GO_BUTTON'}),
            })
          } else {
            Swal.fire({
              title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}),
              text: '',
              icon: 'error',
              confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
            })
          }
        } else {
          if (auth.error == 1) {
            Swal.fire({
              title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_INCOMPLETE'}),
              text: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_INCOMPLETE_TEXT'}),
              icon: 'error',
              confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
            })
          } else if (auth.error == 2) {
            Swal.fire({
              title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_INVALID_CONTACT'}),
              text: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_INVALID_CONTACT_TEXT'}),
              icon: 'error',
              confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
            })
          } else if (auth.error == 3) {
            Swal.fire({
              title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_WRONG_CREDENTIALS'}),
              text: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_WRONG_CREDENTIALS_TEXT'}),
              icon: 'error',
              confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
            })
          } else if (auth.error == 4) {
            Swal.fire({
              title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_WRONG_CREDENTIALS'}),
              text: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_WRONG_CREDENTIALS_TEXT'}),
              icon: 'error',
              confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
            })
          } else {
            Swal.fire({
              title: intl.formatMessage({id: 'AUTH.LOGIN.ERROR_TITLE'}),
              text: '',
              icon: 'error',
              confirmButtonText: intl.formatMessage({id: 'AUTH.LOGIN.CONFIRM_BUTTON'}),
            })
          }
        }
        saveAuth(undefined)
        setLoading(false)
        setSubmitting(false)
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus(intl.formatMessage({id: 'AUTH.LOGIN.ERROR_WRONG_CREDENTIALS_TEXT'}))
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <div className='form-wrapper'>
      <form
        className='form form-inner  p-5 mt-3'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='text-center mb-11'>
          <h1 className=' fw-bolder mb-3'>{intl.formatMessage({id: 'AUTH.LOGIN.TITLE'})}</h1>
        </div>
        {/* begin::Heading */}

        {/* begin::Login options */}

        {/* end::Login options */}

        {/* begin::Separator */}
        <div className='separator separator-content my-14'>
          <span className='w-125px text-black  fw-semibold fs-7'>{intl.formatMessage({id: 'AUTH.LOGIN.WITH_PASSWORD'})}</span>
        </div>
        {/* end::Separator */}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {/* {registrationSuccess && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>
            Use account <strong>admin@demo.com</strong> and password <strong>demo</strong> to
            continue.
          </div>
        </div>
      )} */}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label className='form-label fs-6'>{intl.formatMessage({id: 'AUTH.INPUT.PHONE'})}</label>
          <input
            placeholder={intl.formatMessage({id: 'AUTH.LOGIN.PHONE_PLACEHOLDER'})}
            {...formik.getFieldProps('phone')}
            className={clsx(
              'form-control bg-transparent custom-input',
              {'is-invalid': formik.touched.phone && formik.errors.phone},
              {
                'is-valid': formik.touched.phone && !formik.errors.phone,
              }
            )}
            type='phone'
            name='phone'
            autoComplete='off'
          />
          {formik.touched.phone && formik.errors.phone && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.phone}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <label className='form-label fs-6 mb-2'>{intl.formatMessage({id: 'AUTH.INPUT.PASSWORD'})}</label>
          <input
            placeholder={intl.formatMessage({id: 'AUTH.LOGIN.PASSWORD_PLACEHOLDER'})}
            type='password'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent custom-input',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Wrapper */}
        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
          <div />

          {/* begin::Link */}
          <Link to='/auth/forgot-password' className='link custom-primary-color'>
            {intl.formatMessage({id: 'AUTH.LOGIN.FORGOT_PASSWORD'})}
          </Link>
          {/* end::Link */}
        </div>
        {/* end::Wrapper */}

        {/* begin::Action */}
        <div className='d-grid mb-10'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='custom-btn btn-custom-font btn-custom-color rounded-pill btn-lg w-auto'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'AUTH.LOGIN.BUTTON'})}</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                {intl.formatMessage({id: 'AUTH.LOGIN.PLEASE_WAIT'})}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
          <Link to='/' className='w-100'>
            <button className='w-100 btn btn-custom btn-lg  mt-5'>{intl.formatMessage({id: 'AUTH.LOGIN.BACK_TO_HOME'})}</button>
          </Link>
        </div>
        {/* end::Action */}

        <div className=' text-center fs-6'>
          {intl.formatMessage({id: 'AUTH.GENERAL.NO_ACCOUNT'})}{' '}
          <Link to='/auth/registration' className='link custom-primary-color'>
            {intl.formatMessage({id: 'AUTH.GENERAL.SIGNUP_BUTTON'})}
          </Link>
        </div>
      </form>
    </div>
  )
}
